import React, { useState, useEffect, useRef, useCallback } from "react";
import timesheetApi from "../../../store/services/timesheet.service";
import projectApi from "../../../store/services/projects.service";
import WeeklyTimesheetGrid from "./WeeklyTimesheetGrid";
import CustomButton from "../../../components/controls/button/Button";
import IconInput from "../../../components/controls/input/iconInput/IconInput";
import Searchable from "../../../components/controls/select/CustomSelect";
import { ArrowLeft, SearchIcon, PersonIcon, ProjectIcon, NotFoundIcon } from "../../../modules/default-svg";
import { getLabelByType } from "../../../helpers/utils";
import styles from "../timesheet.module.scss";
import HoverCard from "../../../components/hoverCard/HoverCard";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux"; // ADD useSelector
import { generalActions } from "../../../store/actions";
import NotFoundLayout from "../../../components/controls/NotFound/NotFound";
import { displayDecimalFormatted, getAccountCurrency } from "../../../utils/systemSettings";
import TimesheetExportButton from "./TimesheetExportButton";
import { Lock } from "lucide-react";
import moment from "moment";
import Modal from "../../../components/controls/modal/Modal";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TeamTimesheetList = ({
  year,
  week,
  currentWeek,
  onWeekChange,
  showWeekend,
  setShowWeekend
}) => {
  const [teamData, setTeamData] = useState([]);
  const [filteredTeamData, setFilteredTeamData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [groupBy, setGroupBy] = useState("user"); // "user" or "project"
  const [validatingUserId, setValidatingUserId] = useState(null);
  const [cashflowLineOptions, setCashflowLineOptions] = useState([]);
  const [selectedCashflowLines, setSelectedCashflowLines] = useState({});
  const [overrideRates, setOverrideRates] = useState({});
  const [confirmationModalData, setConfirmationModalData] = useState(null);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const currency = getAccountCurrency(settings);

  const dispatch = useDispatch();

  const displayDays = showWeekend ? DAYS : DAYS.slice(0, 5);
  const displayDaysShort = showWeekend ? DAYS_SHORT : DAYS_SHORT.slice(0, 5);

  useEffect(() => {
    if (year && week) {
      fetchTeamData();
    }
  }, [year, week]);

  useEffect(() => {
    filterTeamData();
  }, [searchTerm, projectSearchTerm, teamData, groupBy]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const response = await timesheetApi.getPendingValidations({
        year,
        week,
      });
      setTeamData(response || []);
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterTeamData = () => {
    let filtered = [...teamData];

    if (searchTerm) {
      filtered = filtered.filter((userData) =>
        userData?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (projectSearchTerm) {
      filtered = filtered.filter((userData) =>
        userData?.project?.name?.toLowerCase().includes(projectSearchTerm.toLowerCase())
      );
    }

    setFilteredTeamData(filtered);
  };

  const safeString = (val) => (val || "").toString();

  const groupData = () => {
    if (groupBy === "user") {
      const grouped = {};

      filteredTeamData.forEach((item) => {
        const userId = item?.user?.id;
        if (!userId) return;

        if (!grouped[userId]) {
          grouped[userId] = {
            user: item.user,
            projects: [],
            totalHours: 0
          };
        }

        grouped[userId].projects.push(item);
        grouped[userId].totalHours += item?.totalHours || 0;
      });

      return Object.values(grouped)
        // sort users by name
        .sort((a, b) =>
          safeString(a?.user?.name).localeCompare(safeString(b?.user?.name))
        )
        // sort projects inside each user
        .map((group) => ({
          ...group,
          projects: group.projects.sort((a, b) =>
            safeString(a?.project?.name).localeCompare(
              safeString(b?.project?.name)
            )
          )
        }));

    } else {
      const grouped = {};

      filteredTeamData.forEach((item) => {
        const projectId = item?.project?.id;
        if (!projectId) return;

        if (!grouped[projectId]) {
          grouped[projectId] = {
            project: item.project,
            users: [],
            totalHours: 0
          };
        }

        grouped[projectId].users.push(item);
        grouped[projectId].totalHours += item?.totalHours || 0;
      });

      return Object.values(grouped)
        // sort projects by name
        .sort((a, b) =>
          safeString(a?.project?.name).localeCompare(
            safeString(b?.project?.name)
          )
        )
        // sort users inside each project
        .map((group) => ({
          ...group,
          users: group.users.sort((a, b) =>
            safeString(a?.user?.name).localeCompare(
              safeString(b?.user?.name)
            )
          )
        }));
    }
  };

  const handleUserClick = (userData) => {
    setSelectedUser(userData.user);
    setSelectedProject(userData.project);
  };

  const handleValidateClick = async (userData) => {
    if (userData.projectCashflowLine) {
      await confirmValidation(userData);
    } else {
      const key = `${userData.user.id}_${userData.project.id}`;
      setValidatingUserId(key);
      setOverrideRates(prev => ({
        ...prev,
        [key]: {
          hourlyRate: userData.hourlyRate ?? 0,
          hourlyBillRate: userData.hourlyBillRate ?? 0,
        }
      }));
      handleCashflowLineSearch("", userData.project.id);
    }
  };

  const handleInvalidateClick = (userData) => {
    setConfirmationModalData({
      title: "Confirm Action",
      body: "Are you sure you want to invalidate this timesheet entry?",
      entryId: userData.id,
    });
  };

  const handleCancel = () => {
    setConfirmationModalData(null);
  }

  const handleInvalidate = async (id) => {
    if (!id) return;
    setConfirmationModalData(null);
    setLoading(true);
    try {

      await timesheetApi.invalidateTimesheet(id);
      await fetchTeamData();
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: "Something went wrong validating timesheet. Please try again later.",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const searchCashflowLineHandler = async (value, projectId) => {
    setCashflowLineOptions([]);
    if (!value) value = "";

    const searchParams = {
      description: value
    };

    try {
      const response = await projectApi.getProjectCashflowLookup(projectId, searchParams);
      setCashflowLineOptions(response?.lines || []);
    } catch (error) {
      console.error("Error fetching cashflow lines:", error);
      setCashflowLineOptions([]);
    }
  };

  const cashflowLineSearchDebounced = useRef(
    debounce((value, projectId) => searchCashflowLineHandler(value, projectId), 500)
  );

  const handleCashflowLineSearch = useCallback((value, projectId) => {
    cashflowLineSearchDebounced.current(value, projectId);
  }, []);

  const handleCancelValidation = (key) => {
    setValidatingUserId(null);
    setCashflowLineOptions([]);
    setSelectedCashflowLines(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setOverrideRates(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleCashflowLineSelect = (key, line) => {
    setSelectedCashflowLines(prev => ({
      ...prev,
      [key]: line?.value
    }));
  };

  const confirmValidation = async (userData) => {
    const key = `${userData.user.id}_${userData.project.id}`;
    const lineId = userData.projectCashflowLine?.id;

    if (!lineId) {
      dispatch(
        generalActions.showToast({
          title: "Resource need to be linked to a Payment code before validating the timesheet.",
          type: "warning",
        })
      );
      return;
    }

    setLoading(true);
    try {
      await timesheetApi.validateTimesheet(userData.id);

      setValidatingUserId(null);
      setCashflowLineOptions([]);
      setSelectedCashflowLines(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      setOverrideRates(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
      await fetchTeamData();
      if (selectedUser?.id === userData.user.id && selectedProject?.id === userData.project.id) {
        setSelectedUser(null);
        setSelectedProject(null);
      }
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: "Something went wrong validating timesheet. Please try again later.",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSuccess = () => {
    fetchTeamData();
  };

  const getStatusLabel = (status) => {
    const labels = { 0: "Draft", 1: "Submitted", 2: "Validated" };
    return labels[status] || "-";
  };

  const formatCurrency = (value) => {
    return `${getAccountCurrency(settings)} ${displayDecimalFormatted(settings, value ?? 0)}`
  };

  const getGroupTotals = (items) => ({
    totalHours: items.reduce((s, i) => s + (i.totalHours || 0), 0),
    maxWeeklyHours: items.reduce((s, i) => s + (i.maxWeeklyHours || 0), 0),
    totalCostRate: items.reduce((s, i) => s + (i.hourlyRate || 0), 0),
    totalCost: items.reduce((s, i) => s + (i.totalHours || 0) * (i.hourlyRate || 0), 0),
    totalBillRate: items.reduce((s, i) => s + (i.hourlyBillRate || 0), 0),
    totalBilled: items.reduce((s, i) => s + (i.totalHours || 0) * (i.hourlyBillRate || 0), 0),
  });

  if (loading) {
    return (
      <div className={styles.teamList}>
        <div className={styles.teamListHeader}>
          <div className={styles.skeletonSearchBar}></div>
        </div>
        <div className={styles.teamTable}>
          <table>
            <thead>
              <tr>
                <th>Resource</th>
                <th>Total Hours</th>
                <th>Cost Rate</th>
                <th>Total Cost</th>
                <th>Bill Rate</th>
                <th>Total Billed</th>
                <th>Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index}>
                  <td><div className={styles.skeletonText}></div></td>
                  <td><div className={styles.skeletonText}></div></td>
                  <td><div className={styles.skeletonText}></div></td>
                  <td><div className={styles.skeletonBadge}></div></td>
                  <td><div className={styles.skeletonBadge}></div></td>
                  <td><div className={styles.skeletonBadge}></div></td>
                  <td><div className={styles.skeletonBadge}></div></td>
                  <td><div className={styles.skeletonBadge}></div></td>
                  <td>
                    <div className={styles.skeletonActions}>
                      <div className={styles.skeletonButton}></div>
                      <div className={styles.skeletonButton}></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (selectedUser && selectedProject) {
    return (
      <div className={styles.userTimesheetView}>
        <div className={styles.backButton}>
          <div
            className="d-flex align-items-center"
            onClick={() => {
              setSelectedUser(null);
              setSelectedProject(null);
            }}
          >
            <ArrowLeft />
            <p className="my-3 text-sm-500 text-[#5e5e5e] hover:text-[#342c82] hover:cursor-pointer">
              Back to Team List
            </p>
          </div>
        </div>
        <div className={styles.userTimesheetViewHeader}>
          <HoverCard
            HoverCardTrigger={
              <Avatar
                color="colorful"
                size={24}
                name={selectedUser.name}
                image={{ src: selectedUser.image }}
              />
            }
            AvatarId={selectedUser.id}
            AvatarName={selectedUser.name}
            AvatarImage={selectedUser.image}
            AvatarDesignation={selectedUser.title || ""}
            AvatarEmail={selectedUser.email}
            AvatarPhone={selectedUser.phoneNumber}
            AvatarIsExternal={selectedUser.isExternal}
            CanSendMessage={true}
          />
          <h3 className={styles.userName}><b className={styles.userNameField}>{selectedUser.name}</b>'s Timesheet - <b className={styles.userNameField}>{selectedProject.name}</b></h3>
        </div>
        <WeeklyTimesheetGrid
          projectId={selectedProject.id}
          year={year}
          week={week}
          isPM={true}
          userId={selectedUser.id}
          currentWeek={currentWeek}
          onWeekChange={onWeekChange}
          showWeekend={showWeekend}
          setShowWeekend={setShowWeekend}
          onSaveSuccess={handleSaveSuccess}
        />
      </div>
    );
  }

  const groupedData = groupData();

  return (
    <>
      <div className={styles.teamList}>
        <div className={styles.teamListHeader}>
          <div className="flex items-center gap-2">
            <IconInput
              icon={<PersonIcon />}
              value={searchTerm}
              placeholder="Search by user"
              handleChange={setSearchTerm}
            />
            <IconInput
              icon={<ProjectIcon />}
              value={projectSearchTerm}
              placeholder="Search by project"
              handleChange={setProjectSearchTerm}
            />
          </div>

          <div className={styles.groupByToggle}>
            <span className={styles.groupByLabel}>Group by</span>
            <label>
              <input
                type="radio"
                name="groupBy"
                value="user"
                checked={groupBy === "user"}
                onChange={(e) => setGroupBy(e.target.value)}
              />
              <span>Resource</span>
            </label>
            <label>
              <input
                type="radio"
                name="groupBy"
                value="project"
                checked={groupBy === "project"}
                onChange={(e) => setGroupBy(e.target.value)}
              />
              <span>Project</span>
            </label>
          </div>
          <label className={styles.weekendToggle}>
            <input
              type="checkbox"
              checked={showWeekend}
              onChange={(e) => setShowWeekend(e.target.checked)}
            />
            <span>Show Weekend</span>
          </label>
          <TimesheetExportButton />
        </div>

        <div className={styles.teamTable}>
          <table>
            <thead>
              <tr>
                <th>{groupBy === "user" ? "Resource / Project" : "Project / Resource"}</th>
                {displayDays.map((day, index) => {
                  const dayIndex = DAYS.indexOf(day);
                  const dayDate = currentWeek
                    ? moment(currentWeek.startDate).add(dayIndex, 'days').format('DD')
                    : '';

                  return (
                    <th key={day}>{`${displayDaysShort[index]} `}
                      <span className={styles.compactTeamViewDayDate}>{dayDate}</span>
                    </th>
                  );
                })}
                <th>Total Hours</th>
                <th>Cost Rate</th>
                <th>Total Cost</th>
                <th>Bill Rate</th>
                <th>Total Billed</th>
                <th>Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupedData.length === 0 ? (
                <tr>
                  <td colSpan={displayDays.length + 7} className={styles.emptyRow}>
                    <NotFoundLayout
                      icon={<NotFoundIcon />}
                      heading="No records found"
                    />
                  </td>
                </tr>
              ) : (
                groupedData.map((group) => {
                  const items = groupBy === "user" ? group.projects : group.users;
                  const totals = getGroupTotals(items);
                  const groupHoursLabel = `${Number(totals.totalHours.toFixed(2))} / ${Number(totals.maxWeeklyHours.toFixed(2))}h`;
                  const isOver = totals.totalHours > 0 && totals.totalHours > totals.maxWeeklyHours;

                  return (
                    <React.Fragment key={groupBy === "user" ? group.user.id : group.project.id}>
                      {groupBy === "user" ? (
                        <>
                          <tr className={styles.groupHeaderRow}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <HoverCard
                                  HoverCardTrigger={
                                    <Avatar
                                      color="colorful"
                                      size={24}
                                      name={group.user.name}
                                      image={{ src: group.user.image }}
                                    />
                                  }
                                  AvatarId={group.user.id}
                                  AvatarName={group.user.name}
                                  AvatarImage={group.user.image}
                                  AvatarDesignation={group.user.title || ""}
                                  AvatarEmail={group.user.email}
                                  AvatarPhone={group.user.phoneNumber}
                                  AvatarIsExternal={group.user.isExternal}
                                  CanSendMessage={true}
                                />

                                <div className={styles.userInfo}>
                                  <strong className={styles.groupHeaderText}>{group.user.name}</strong>
                                  {group.user.title && <span className={styles.userTitle}>{group.user.title}</span>}
                                </div>
                              </div>
                            </td>
                            <td colSpan={displayDays.length}></td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={`${styles.totalHoursValue} ${isOver && styles.hoursOver}`}>{groupHoursLabel}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalCostRate)}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalCost)}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalBillRate)}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalBilled)}</span>
                            </td>
                            <td colSpan={3}></td>
                          </tr>
                          {/* Grouping by User */}
                          {group.projects.map((userData) => {
                            const key = `${userData.user.id}_${userData.project.id}`;
                            const isValidating = validatingUserId === key;
                            const totalCostRate = (userData.hourlyRate || 0);
                            const totalCost = (userData.totalHours || 0) * (userData.hourlyRate || 0);
                            const totalBillRate = (userData.hourlyBillRate || 0);
                            const totalBilled = (userData.totalHours || 0) * (userData.hourlyBillRate || 0);
                            const hoursLabel = `${Number((userData.totalHours || 0).toFixed(2))} / ${Number(userData.maxWeeklyHours.toFixed(2))}h`;
                            const isOver = userData.totalHours > 0 && userData.totalHours > userData.maxWeeklyHours;
                            const rates = overrideRates[key] || {};

                            return (
                              <React.Fragment key={key}>
                                <tr className={`${styles.groupRow} ${isValidating ? styles.validatingRow : ''}`}>
                                  <td>
                                    <div
                                      className={`d-flex align-items-center gap-2 ps-4 ${userData.statusSummary !== 2 ? "cursor-pointer" : "cursor-not-allowed"}`}
                                      onClick={() => userData.statusSummary !== 2 && handleUserClick(userData)}
                                    >
                                      <span className={styles.projectName}>{userData.project.name}</span>
                                    </div>
                                  </td>
                                  {displayDays.map((day, index) => {
                                    const dayHours = userData.entries?.[index]?.hours || 0;
                                    return (
                                      <td key={day}>
                                        <span className={styles.dayHours}>
                                          {dayHours > 0 ? Number(dayHours.toFixed(2)) : '-'}
                                        </span>
                                      </td>
                                    );
                                  })}
                                  <td>
                                    <span className={isOver ? `${styles.hoursValue} ${styles.hoursOver}` : styles.hoursValue}>
                                      {hoursLabel}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalCostRate)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalCost)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalBillRate)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalBilled)}</span>
                                  </td>
                                  <td>
                                    {userData.projectCashflowLine ? (
                                      <span className={styles.codeValue}>{userData.projectCashflowLine?.name}</span>
                                    ) : (
                                      <span className={styles.emptyCodeValue}>-</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className={styles.statusBadge}>
                                      <span className={styles[`status${userData.statusSummary}`]}>
                                        {getStatusLabel(userData.statusSummary)}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className={styles.actionButtons}>
                                      {
                                        !userData.isTimesheetEntryAllowed ? (
                                          <Tooltip
                                            content={`Timesheet entry locked for this project`}
                                            relationship="label"
                                          >
                                            <Lock size={16} color="#342c82" strokeWidth={1} />
                                          </Tooltip>
                                        ) : (
                                          userData.statusSummary !== 2 ? (
                                            <>
                                              <CustomButton btnClass={styles.viewButton} label="Manage" clickHandler={() => handleUserClick(userData)} />
                                              {userData.statusSummary === 1 && !isValidating && (
                                                <CustomButton btnClass={styles.submitButton} label="Validate" clickHandler={() => confirmValidation(userData)} />
                                              )}
                                              {isValidating && (
                                                <CustomButton btnClass={styles.cancelButton} label="Cancel" clickHandler={() => handleCancelValidation(key)} />
                                              )}
                                            </>
                                          ) :
                                            <CustomButton btnClass={styles.cancelButton} label="Invalidate" clickHandler={() => handleInvalidateClick(userData)} />
                                        )
                                      }
                                    </div>
                                  </td>
                                </tr>
                                {isValidating && (
                                  <tr className={styles.validationRow}>
                                    <td colSpan={displayDays.length + 7}>
                                      <div className={styles.validationContent}>
                                        {/* <div className={styles.validationLabel}>Select Code</div>
                                        <div className={styles.validationField}>
                                          <Searchable
                                            placeholder="Search code..."
                                            SelectText="Code"
                                            forceRenderDefaultVal={false}
                                            isClearable={false}
                                            defaultVal={userData.projectCashflowLine ?
                                              {
                                                label: `${getLabelByType("cashflowLineCategory", userData.projectCashflowLine.category)} - ${userData.projectCashflowLine.name}`,
                                                value: userData.projectCashflowLine.id,
                                              }
                                              : null}
                                            searchText={(value) => handleCashflowLineSearch(value, userData.project.id)}
                                            options={cashflowLineOptions.map((option) => ({
                                              label: `${getLabelByType("cashflowLineCategory", option.category)} - ${option.description}`,
                                              value: option.id,
                                            }))}
                                            onChange={(line) => handleCashflowLineSelect(key, line)}
                                            className={styles.cashflowSelect}
                                          />
                                        </div> */}
                                        <div className={styles.validationLabel}>Hourly Rate</div>
                                        <div className={styles.validationHourField}>
                                          <div className={styles.rateInputWrapper}>
                                            <span className={styles.rateCurrencyPrefix}>{currency}</span>
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              className={styles.rateInput}
                                              value={rates.hourlyRate ?? ""}
                                              disabled
                                              // onChange={(e) =>
                                              //   setOverrideRates(prev => ({
                                              //     ...prev,
                                              //     [key]: { ...prev[key], hourlyRate: parseFloat(e.target.value) || 0 }
                                              //   }))
                                              // }
                                            />
                                          </div>
                                        </div>
                                        <div className={styles.validationLabel}>Bill Rate</div>
                                        <div className={styles.validationHourField}>
                                          <div className={styles.rateInputWrapper}>
                                            <span className={styles.rateCurrencyPrefix}>{currency}</span>
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              className={styles.rateInput}
                                              value={rates.hourlyBillRate ?? ""}
                                              disabled
                                              // onChange={(e) =>
                                              //   setOverrideRates(prev => ({
                                              //     ...prev,
                                              //     [key]: { ...prev[key], hourlyBillRate: parseFloat(e.target.value) || 0 }
                                              //   }))
                                              // }
                                            />
                                          </div>
                                        </div>

                                        <div className={styles.validationActions}>
                                          <CustomButton
                                            btnClass={styles.confirmButton}
                                            label="Confirm"
                                            clickHandler={() => confirmValidation(userData)}
                                            isDisabled={!(userData.projectCashflowLine || selectedCashflowLines[key]) || loading}
                                            showLoading={loading}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          <tr className={styles.groupHeaderRow}>
                            <td>
                              <strong className={styles.groupHeaderText}>{group.project.name}</strong>
                            </td>
                            <td colSpan={displayDays.length}></td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={`${styles.totalHoursValue} ${isOver && styles.hoursOver}`}>{groupHoursLabel}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalCost)}</span>
                            </td>
                            <td className={styles.totalHoursValueContainer}>
                              <span className={styles.totalHoursValue}>{formatCurrency(totals.totalBilled)}</span>
                            </td>
                            <td colSpan={3}></td>
                          </tr>
                          {/* Grouping by Project */}
                          {group.users.map((userData) => {
                            const key = `${userData.user.id}_${userData.project.id}`;
                            const isValidating = validatingUserId === key;
                            const totalCostRate = (userData.hourlyRate || 0);
                            const totalCost = (userData.totalHours || 0) * (userData.hourlyRate || 0);
                            const totalBillRate = (userData.hourlyBillRate || 0);
                            const totalBilled = (userData.totalHours || 0) * (userData.hourlyBillRate || 0);
                            const hoursLabel = `${Number((userData.totalHours || 0).toFixed(2))} / ${Number(userData.maxWeeklyHours.toFixed(2))}h`;
                            const isOver = userData.totalHours && (userData.totalHours > userData.maxWeeklyHours);
                            const rates = overrideRates[key] || {};

                            return (
                              <React.Fragment key={key}>
                                <tr className={`${styles.groupRow} ${isValidating ? styles.validatingRow : ''}`}>
                                  <td>
                                    <div
                                      className={`d-flex align-items-center gap-2 ps-4 ${userData.statusSummary !== 2 ? "cursor-pointer" : "cursor-not-allowed"}`}
                                      onClick={() => userData.statusSummary !== 2 && handleUserClick(userData)}
                                    >
                                      <HoverCard
                                        HoverCardTrigger={
                                          <Avatar
                                            color="colorful"
                                            size={24}
                                            name={userData.user.name}
                                            image={{ src: userData.user.image }}
                                          />
                                        }
                                        AvatarId={userData.user.id}
                                        AvatarName={userData.user.name}
                                        AvatarImage={userData.user.image}
                                        AvatarDesignation={userData.user.title || ""}
                                        AvatarEmail={userData.user.email}
                                        AvatarPhone={userData.user.phoneNumber}
                                        AvatarIsExternal={userData.user.isExternal}
                                        CanSendMessage={true}
                                      />
                                      <div className={styles.userInfo}>
                                        <span className={styles.userName}>{userData.user.name}</span>
                                        {userData.user.title && <span className={styles.userTitle}>{userData.user.title}</span>}
                                      </div>
                                    </div>
                                  </td>
                                  {displayDays.map((day, index) => {
                                    const dayHours = userData.entries?.[index]?.hours || 0;
                                    return (
                                      <td key={day}>
                                        <span className={styles.dayHours}>
                                          {dayHours > 0 ? Number(dayHours.toFixed(2)) : '-'}
                                        </span>
                                      </td>
                                    );
                                  })}
                                  <td>
                                    <span className={isOver ? `${styles.hoursValue} ${styles.hoursOver}` : styles.hoursValue}>
                                      {hoursLabel}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalCostRate)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalCost)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalBillRate)}</span>
                                  </td>
                                  <td>
                                    <span className={styles.costValue}>{formatCurrency(totalBilled)}</span>
                                  </td>
                                  <td>
                                    {userData.projectCashflowLine ? (
                                      <span className={styles.codeValue}>{userData.projectCashflowLine?.name}</span>
                                    ) : (
                                      <span className={styles.emptyCodeValue}>-</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className={styles.statusBadge}>
                                      <span className={styles[`status${userData.statusSummary}`]}>
                                        {getStatusLabel(userData.statusSummary)}
                                      </span>
                                    </span>
                                  </td>
                                  <td>
                                    <div className={styles.actionButtons}>
                                      {
                                        !userData.isTimesheetEntryAllowed ? (
                                          <Tooltip
                                            content={`Timesheet entry locked for this resource`}
                                            relationship="label"
                                          >
                                            <Lock size={16} color="#342c82" strokeWidth={1} />
                                          </Tooltip>
                                        ) : (
                                          userData.statusSummary !== 2 ? (
                                            <>
                                              <CustomButton btnClass={styles.viewButton} label="Manage" clickHandler={() => handleUserClick(userData)} />
                                              {userData.statusSummary === 1 && !isValidating && (
                                                <CustomButton btnClass={styles.submitButton} label="Validate" clickHandler={() => confirmValidation(userData)} />
                                              )}
                                              {isValidating && (
                                                <CustomButton btnClass={styles.cancelButton} label="Cancel" clickHandler={() => handleCancelValidation(key)} />
                                              )}
                                            </>
                                          ) :
                                            <CustomButton btnClass={styles.cancelButton} label="Invalidate" clickHandler={() => handleInvalidateClick(userData)} />
                                        )
                                      }
                                    </div>
                                  </td>
                                </tr>
                                {isValidating && (
                                  <tr className={styles.validationRow}>
                                    <td colSpan={displayDays.length + 7}>
                                      <div className={styles.validationContent}>
                                        {/* <div className={styles.validationLabel}>Select Code:</div>
                                        <div className={styles.validationField}>
                                          <Searchable
                                            placeholder="Search code..."
                                            SelectText="Code"
                                            isClearable={false}
                                            forceRenderDefaultVal={false}
                                            defaultVal={userData.projectCashflowLine ?
                                              {
                                                label: `${getLabelByType("cashflowLineCategory", userData.projectCashflowLine.category)} - ${userData.projectCashflowLine.name}`,
                                                value: userData.projectCashflowLine.id,
                                              }
                                              : null}
                                            searchText={(value) => handleCashflowLineSearch(value, userData.project.id)}
                                            options={cashflowLineOptions.map((option) => ({
                                              label: `${getLabelByType("cashflowLineCategory", option.category)} - ${option.description}`,
                                              value: option.id,
                                            }))}
                                            onChange={(line) => handleCashflowLineSelect(key, line)}
                                            className={styles.cashflowSelect}
                                          />
                                        </div> */}
                                        <div className={styles.rateOverrideFields}>
                                          <div className={styles.rateOverrideField}>
                                            <label className={styles.rateOverrideLabel}>Hourly Rate</label>
                                            <div className={styles.rateInputWrapper}>
                                              <span className={styles.rateCurrencyPrefix}>{currency}</span>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className={styles.rateInput}
                                                value={rates.hourlyRate ?? ""}
                                                disabled
                                                // onChange={(e) =>
                                                //   setOverrideRates(prev => ({
                                                //     ...prev,
                                                //     [key]: { ...prev[key], hourlyRate: parseFloat(e.target.value) || 0 }
                                                //   }))
                                                // }
                                              />
                                            </div>
                                          </div>
                                          <div className={styles.rateOverrideField}>
                                            <label className={styles.rateOverrideLabel}>Bill Rate</label>
                                            <div className={styles.rateInputWrapper}>
                                              <span className={styles.rateCurrencyPrefix}>{currency}</span>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className={styles.rateInput}
                                                value={rates.hourlyBillRate ?? ""}
                                                disabled
                                                // onChange={(e) =>
                                                //   setOverrideRates(prev => ({
                                                //     ...prev,
                                                //     [key]: { ...prev[key], hourlyBillRate: parseFloat(e.target.value) || 0 }
                                                //   }))
                                                // }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <div className={styles.validationActions}>
                                          <CustomButton
                                            btnClass={styles.confirmButton}
                                            label="Confirm"
                                            clickHandler={() => confirmValidation(userData)}
                                            isDisabled={!(userData.projectCashflowLine || selectedCashflowLines[key]) || loading}
                                            showLoading={loading}
                                          />
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {confirmationModalData && (
        <Modal
          title={confirmationModalData?.title || "Confirm Action"}
          body={confirmationModalData?.body || "Are you sure you want to proceed?"}
          handleCancel={() => handleCancel()}
          handleSave={() => handleInvalidate(confirmationModalData.entryId)}
          saveBtnLabel={confirmationModalData?.saveBtnLabel || "Confirm"}
          cancelBtnLabel={confirmationModalData?.cancelBtnLabel || "Cancel"}
          isLoading={confirmationModalData?.isLoading || false}
        />
      )}
    </>
  );
};

export default TeamTimesheetList;
